/**
 * Argus — agent version status + history + control hooks (TanStack Query).
 *
 * Bloc agent-version-display 2026-05-06.
 */

import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './api';

export interface AgentVersionStatus {
  agentId: string;
  currentVersion: string | null;
  currentChannel: 'dev' | 'staging' | 'prod' | null;
  latestVersionOnChannel: string | null;
  isAtLatest: boolean;
  isOutdated: boolean;
  isBlocked: boolean;
  updateDisabled: boolean;
  channelFrozen: boolean;
  isOnline: boolean;
  // Bloc agent-update-eta 2026-05-07
  lastUpdateCheckAt: string | null;
  lastUpdateCheckSecondsAgo: number | null;
  checkIntervalSeconds: number;
  nextCheckEtaSeconds: number | null;
}

export interface AgentUpdateHistoryItem {
  id: number;
  manifestId: string;
  fromVersion: string | null;
  toVersion: string;
  status: 'downloading' | 'verifying' | 'installing' | 'success' | 'failed' | 'rolled_back';
  errorMessage: string | null;
  startedAt: string;
  completedAt: string | null;
}

export interface AgentUpdateHistoryResponse {
  agentId: string;
  items: AgentUpdateHistoryItem[];
  nextCursor: number | null;
  limit: number;
}

export function useAgentVersionStatus(agentId: string | undefined) {
  const qc = useQueryClient();

  // Auto-invalidate la primire `argus:agent-status` (online/offline) sau `argus:agent-version`
  // (post-update version change detect server-side în heartbeat). Ambele pot schimba isOutdated/isAtLatest.
  useEffect(() => {
    function handler(ev: Event) {
      const ce = ev as CustomEvent<{ agentId: string }>;
      if (ce.detail?.agentId?.toUpperCase() === agentId?.toUpperCase()) {
        void qc.invalidateQueries({ queryKey: ['agentVersionStatus', agentId] });
        void qc.invalidateQueries({ queryKey: ['agentUpdateHistory', agentId] });
        void qc.invalidateQueries({ queryKey: ['agents'] });
      }
    }
    window.addEventListener('argus:agent-status', handler);
    window.addEventListener('argus:agent-version', handler);
    return () => {
      window.removeEventListener('argus:agent-status', handler);
      window.removeEventListener('argus:agent-version', handler);
    };
  }, [agentId, qc]);

  return useQuery({
    queryKey: ['agentVersionStatus', agentId],
    queryFn: async () => {
      const res = await apiFetch<AgentVersionStatus>(`/api/v1/agents/${agentId}/version-status`);
      return res.data;
    },
    enabled: agentId !== undefined && agentId !== '',
    refetchInterval: 30_000,
  });
}

export function useAgentUpdateHistory(agentId: string | undefined, page = 1) {
  return useQuery({
    queryKey: ['agentUpdateHistory', agentId, page],
    queryFn: async () => {
      const res = await apiFetch<AgentUpdateHistoryResponse>(
        `/api/v1/agents/${agentId}/update-history?limit=25`
      );
      return res.data;
    },
    enabled: agentId !== undefined && agentId !== '',
    refetchInterval: 10_000,
  });
}

export function useToggleAutoUpdateMutation(agentId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (disabled: boolean) => {
      const res = await apiFetch<{ agentId: string; updateDisabled: boolean }>(
        `/api/v1/agents/${agentId}/auto-update`,
        {
          method: 'PATCH',
          body: JSON.stringify({ disabled }),
        }
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['agentVersionStatus', agentId] });
    },
  });
}

export function useSetChannelMutation(agentId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (channel: 'dev' | 'staging' | 'prod') => {
      const res = await apiFetch<{ agentId: string; channel: string; previousChannel: string }>(
        `/api/v1/agents/${agentId}/channel`,
        {
          method: 'PATCH',
          body: JSON.stringify({ channel }),
        }
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['agentVersionStatus', agentId] });
      void qc.invalidateQueries({ queryKey: ['agents'] });
    },
  });
}

export function useForceUpdateMutation(agentId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await apiFetch<{ agentId: string; pushed: boolean }>(
        `/api/v1/agents/${agentId}/force-update`,
        { method: 'POST' }
      );
      return res.data;
    },
    onSuccess: () => {
      // Update history may add a new row in 5-15s
      setTimeout(() => {
        void qc.invalidateQueries({ queryKey: ['agentUpdateHistory', agentId] });
        void qc.invalidateQueries({ queryKey: ['agentVersionStatus', agentId] });
      }, 5000);
    },
  });
}
